import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { LossExposureSection } from '../components/LossExposureSection';
import type {
  CustomerRegisterItemStatus,
  Framework,
  MaturityLevel,
} from '@aigovops/core';
import {
  Alert,
  Badge,
  Button,
  Field,
  PageHeader,
  SeverityBadge,
  Spinner,
  StatusBadge,
  Textarea,
} from '@aigovops/design-system';
import { useRequiredProfile } from '../lib/auth';
import { useOptionalWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import {
  getRegisterItemDetail,
  getAssessment,
  getControlEvidence,
  getProfile,
  setRequiredMaturity,
  updateItemStatus,
  listWorkspaceMembers,
  assignRegisterItem,
  getRegisterItemEvidence,
  listEvidenceArtifacts,
  attachEvidenceArtifact,
  attestEvidenceBinding,
  setEvidenceMaturityOverride,
  listControlChecks,
  ApiError,
} from '../lib/api';
import type { ControlEvidenceResponse } from '../lib/api';
import { categoryLabel, categoryScope, fmtDate, statusLabel } from '../lib/format';
import { QaThreadsSection } from '../components/register/QaThreadsSection';
import { EvidenceRequestsSection } from '../components/register/EvidenceRequestsSection';
import { AssessorAnnotationsSection } from '../components/register/AssessorAnnotationsSection';
import { EvidenceProvenance } from '../components/EvidenceProvenance';

interface AssigneeState {
  user_id: string;
  email: string;
  display_name: string;
}
interface RequiredMaturityOverride {
  level: MaturityLevel;
  reason: string | null;
  set_at: string | null;
  set_by_user_id: string | null;
}
interface WorkspaceMember {
  user_id: string;
  email: string;
  display_name: string;
  role: string;
}

const STATUS_OPTIONS: CustomerRegisterItemStatus[] = [
  'todo',
  'in_progress',
  'blocked',
  'review_needed',
  'done',
  'not_applicable',
  'delegate_up',
];

export function RegisterItemDetail() {
  const profileId = useRequiredProfile();
  const workspaceCtx = useOptionalWorkspace();
  const workspaceSlug = workspaceCtx?.workspace.slug ?? null;
  const canManage =
    workspaceCtx?.role === 'owner' ||
    workspaceCtx?.role === 'admin' ||
    workspaceCtx?.role === 'editor';

  // PR-P3-2: AlertDetail links here with `?from=alert-<id>` (UC-4
  // acceptance — "Open in Register" preserves the round-trip back to
  // the alert that led here). Any other `?from` value falls back to
  // the plain register list, same as no param at all.
  const [searchParams] = useSearchParams();
  const fromAlertId = searchParams.get('from')?.match(/^alert-(.+)$/)?.[1] ?? null;

  const { id } = useParams<{ id: string }>();

  // V9-4-2: when workspace context exists, use the composite endpoint
  // that returns item + state + control-evidence + frameworks in one
  // round-trip. Falls back to the individual getRegisterItem call when
  // there's no workspace (legacy /app/register/:id path).
  const detail = useWorkspaceQuery(
    (slug) => queryKeys.registerItemFull(slug, id!),
    (slug) => getRegisterItemDetail(slug, id!),
  );
  // Derive the old `item` + `frameworks` shapes from the composite so
  // downstream code sees no change.
  const item = useMemo(() => ({
    loading: detail.loading,
    error: detail.error,
    data: detail.data ? { item: detail.data.item, frameworkMappings: detail.data.frameworkMappings } : null,
    refetch: detail.refetch,
  }), [detail]);
  const frameworks = useMemo(() => ({
    loading: detail.loading,
    error: detail.error,
    data: detail.data ? detail.data.frameworks : null,
    refetch: detail.refetch,
  }), [detail]);

  const assess = useWorkspaceQuery(
    (slug) => queryKeys.wsAssessment(slug, profileId),
    () => getAssessment(profileId),
  );
  // Phase 5: pull the workspace's compliance_posture + applicable
  // frameworks so the Suggested-targets panel knows which frameworks
  // to emphasize vs. dim as reference.
  const profile = useWorkspaceQuery(
    (slug) => queryKeys.wsProfile(slug, profileId),
    () => getProfile(profileId),
  );
  const [pendingStatus, setPendingStatus] = useState<CustomerRegisterItemStatus | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [assignee, setAssignee] = useState<AssigneeState | null>(null);
  const [members, setMembers] = useState<WorkspaceMember[] | null>(null);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  // Phase 2b: per-workspace required-maturity override for this item.
  // Layer 1 of the effective-required-level stack — nullable, falls back
  // to editorial framework target then to evidence-type-derived floor.
  const [requiredOverride, setRequiredOverride] =
    useState<RequiredMaturityOverride | null>(null);

  // V9-4-2: hydrate assignee + required-maturity-override from the
  // composite response instead of a separate useEffect + fetch.
  useEffect(() => {
    if (!detail.data?.state) return;
    setAssignee(detail.data.state.assigned_to);
    setRequiredOverride(detail.data.state.required_maturity_override as RequiredMaturityOverride | null);
  }, [detail.data?.state]);

  // Load workspace members for the assignee picker. This is a lightweight
  // call already cached by TanStack Query; keeping it separate avoids
  // bloating the composite with member data most pages don't need.
  useEffect(() => {
    if (!workspaceSlug) return;
    let cancelled = false;
    listWorkspaceMembers(workspaceSlug)
      .then((body) => { if (!cancelled) setMembers(body.members); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [workspaceSlug]);

  const saveAssignee = useCallback(
    async (nextUserId: string | null) => {
      if (!workspaceSlug || !id) return;
      setAssignSaving(true);
      setAssignError(null);
      try {
        await assignRegisterItem(workspaceSlug, id, nextUserId);
        if (nextUserId === null) {
          setAssignee(null);
        } else {
          const m = members?.find((x) => x.user_id === nextUserId) ?? null;
          setAssignee(
            m
              ? { user_id: m.user_id, email: m.email, display_name: m.display_name }
              : null
          );
        }
      } catch (err) {
        setAssignError(err instanceof ApiError ? err.message : "Couldn't save assignee.");
      } finally {
        setAssignSaving(false);
      }
    },
    [workspaceSlug, id, members]
  );

  if (item.loading || assess.loading) return <Spinner label="Loading item" />;
  if (item.error) return <Alert variant="error">{item.error}</Alert>;
  if (!item.data) return null;

  const { item: ri, frameworkMappings } = item.data;
  const gap = assess.data?.gap_rows.find((g) => g.item_id === ri.item_id);
  const currentStatus = gap?.status ?? 'todo';
  const priorityWeight = gap?.priority_weight;
  const displayStatus = pendingStatus ?? currentStatus;

  const parsedFrameworkRefs: string[] = safeParseArray(ri.framework_refs);
  const parsedJurisdictions: string[] = safeParseArray(ri.jurisdiction);
  const parsedClassifications: string[] = safeParseArray(ri.classification_applicability);
  const parsedControls: string[] = safeParseArray(ri.control_ids);

  async function save() {
    if (!pendingStatus) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateItemStatus(profileId, ri.item_id, {
        status: pendingStatus,
        notes: notes || undefined,
      });
      assess.refetch();
      setPendingStatus(null);
      setNotes('');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)', maxWidth: '64rem' }}>
      <Link
        to={
          fromAlertId && workspaceCtx
            ? `/w/${workspaceCtx.workspace.slug}/app/alerts/${fromAlertId}`
            : workspaceCtx
              ? `/w/${workspaceCtx.workspace.slug}/app/register`
              : '/app/register' /* scope-check-ignore: legacy fallback */
        }
        style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--secondary)', textDecoration: 'none' }}
      >
        {fromAlertId ? '← Back to alert' : '← Back to register'}
      </Link>

      <PageHeader
        title={ri.title}
        subtitle={
          <span
            style={{
              display: 'inline-flex',
              gap: 'var(--space-2)',
              alignItems: 'center',
              fontFamily: 'var(--font-mono)',
              color: 'var(--text-dim)',
            }}
          >
            <span>{ri.item_id}</span>
            <span aria-hidden="true">·</span>
            <span>{categoryLabel[ri.category]}</span>
          </span>
        }
        right={
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <SeverityBadge severity={ri.severity} />
            <StatusBadge status={currentStatus} />
            {priorityWeight !== undefined ? <Badge>Priority {priorityWeight}</Badge> : null}
            {ri.effective_date ? <Badge>Effective {fmtDate(ri.effective_date)}</Badge> : null}
          </div>
        }
      />

      <section
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          padding: 'var(--space-4)',
          fontFamily: 'var(--font-ui)',
        }}
      >
        <p style={{ margin: 0, color: 'var(--text)', whiteSpace: 'pre-wrap', fontSize: 'var(--font-size-2xs)' }}>
          {ri.description}
        </p>
        {ri.source_url ? (
          <p style={{ margin: 'var(--space-3) 0 0', fontSize: 'var(--font-size-3xs)' }}>
            <a href={ri.source_url} target="_blank" rel="noreferrer" style={{ color: 'var(--secondary)' }}>
              Source ↗
            </a>
          </p>
        ) : null}
      </section>

      {/* Topic scope — helps the user understand what this category covers,
          especially when drafting a new policy to address open controls. */}
      <section
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          padding: 'var(--space-4)',
          fontFamily: 'var(--font-ui)',
          display: 'grid',
          gap: 'var(--space-2)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>
          Topic scope — {categoryLabel[ri.category]}
        </h2>
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)', lineHeight: 'var(--line-height-body)' }}>
          {categoryScope[ri.category]}
        </p>
        {parsedControls.length > 0 ? (
          <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
            {parsedControls.length} control{parsedControls.length === 1 ? '' : 's'} mapped ({parsedControls.slice(0, 5).join(', ')}{parsedControls.length > 5 ? `, +${parsedControls.length - 5} more` : ''})
          </p>
        ) : null}
        {parsedFrameworkRefs.length > 0 ? (
          <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
            Frameworks: {parsedFrameworkRefs.join(', ')}
          </p>
        ) : null}
        {canManage && workspaceSlug ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Link
              to={`/w/${workspaceSlug}/app/policies/new?category=${encodeURIComponent(ri.category)}`}
              style={{
                fontSize: 'var(--font-size-3xs)',
                color: 'var(--accent)',
                textDecoration: 'none',
                fontWeight: 500,
              }}
            >
              Draft a policy for {categoryLabel[ri.category]} →
            </Link>
          </div>
        ) : null}
      </section>

      {canManage ? <section
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          padding: 'var(--space-4)',
          fontFamily: 'var(--font-ui)',
          display: 'grid',
          gap: 'var(--space-3)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>Update status</h2>
        {saveError ? <Alert variant="error">{saveError}</Alert> : null}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 'var(--space-2)' }}>
          {STATUS_OPTIONS.map((s) => (
            <Button
              key={s}
              variant={displayStatus === s ? 'primary' : 'secondary'}
              aria-pressed={displayStatus === s}
              onClick={() => setPendingStatus(s)}
            >
              {statusLabel[s]}
            </Button>
          ))}
        </div>
        <Field id="notes" label="Notes (optional)">
          <Textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Evidence, blockers, owners, ticket link..."
          />
        </Field>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button
            variant="secondary"
            onClick={() => {
              setPendingStatus(null);
              setNotes('');
            }}
            disabled={!pendingStatus || saving}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={save} disabled={!pendingStatus || saving}>
            {saving ? 'Saving…' : 'Save status'}
          </Button>
        </div>
      </section> : null}

      {workspaceSlug ? (
        <AssigneeSection
          assignee={assignee}
          members={members}
          canManage={canManage}
          saving={assignSaving}
          error={assignError}
          onAssign={saveAssignee}
        />
      ) : null}

      <SuggestedTargetsSection
        item={ri}
        appliesToFrameworks={parsedFrameworkRefs}
        pursuedFrameworks={profile.data?.profile.compliance_posture ?? []}
        applicableFrameworks={profile.data?.applicable_frameworks ?? []}
        allFrameworks={frameworks.data ?? []}
      />

      {workspaceSlug ? (
        <RequiredMaturitySection
          workspaceSlug={workspaceSlug}
          registerItemId={ri.item_id}
          canManage={canManage}
          override={requiredOverride}
          onChanged={setRequiredOverride}
        />
      ) : null}

      {workspaceSlug ? (
        <LossExposureSection workspaceSlug={workspaceSlug} registerItemId={ri.item_id} canManage={canManage} />
      ) : null}

      {workspaceSlug ? (
        <EvidenceSection
          workspaceSlug={workspaceSlug}
          registerItemId={ri.item_id}
          canManage={canManage}
          expectedEvidenceTypesJson={ri.expected_evidence_types}
          category={ri.category}
        />
      ) : null}

      {workspaceSlug ? <LiveCheckSection workspaceSlug={workspaceSlug} controlId={ri.item_id} /> : null}

      {workspaceSlug ? <ConnectorEvidenceIndicator workspaceSlug={workspaceSlug} itemId={ri.item_id} /> : null}

      {/* PR-Cluster-6-b: assessor Q&A on this register item. Threads
          are read + reply for workspace members; the assessor session
          drives thread-opening from its own scoped surface. */}
      {workspaceSlug ? <QaThreadsSection registerItemId={ri.item_id} /> : null}

      {/* PR-Cluster-6-c: assessor evidence requests. Workspace member
          attaches an evidence artifact + closes; assessor drives the
          open + escalate-to-poam paths from its own scoped surface. */}
      {workspaceSlug ? <EvidenceRequestsSection registerItemId={ri.item_id} /> : null}

      {/* PR-Cluster-6-e: assessor verdicts (accepted / needs_remediation
          / not_applicable). Layered separately from the workspace's
          own item status so the assessor's opinion doesn't flip
          customer state. Self-hides when no verdicts exist. */}
      {workspaceSlug ? <AssessorAnnotationsSection registerItemId={ri.item_id} /> : null}

      <section style={{ display: 'grid', gap: 'var(--space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', background: 'var(--surface)', padding: 'var(--space-4)', fontFamily: 'var(--font-ui)' }}>
          <details>
            <summary style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)', marginBottom: 'var(--space-3)', fontWeight: 600, cursor: 'pointer' }}>
              Scope
            </summary>
            <div style={{ display: 'grid', gap: 'var(--space-2)', fontSize: 'var(--font-size-2xs)' }}>
              <FactRow label="Jurisdictions" value={parsedJurisdictions.join(', ')} />
              <FactRow label="Applies to" value={parsedClassifications.join(', ')} />
              <FactRow label="Frameworks" value={parsedFrameworkRefs.join(', ')} />
              <FactRow
                label="Source"
                value={
                  <span>
                    <span style={{ textTransform: 'uppercase', fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-3xs)', marginRight: 'var(--space-2)' }}>
                      {ri.source_type}
                    </span>
                    {ri.source_url ? (
                      <a href={ri.source_url} target="_blank" rel="noreferrer" style={{ color: 'var(--secondary)' }}>
                        link ↗
                      </a>
                    ) : (
                      '—'
                    )}
                  </span>
                }
              />
              <FactRow label="Effective date" value={ri.effective_date ? fmtDate(ri.effective_date) : '—'} />
            </div>
          </details>
        </div>

        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', background: 'var(--surface)', padding: 'var(--space-4)', fontFamily: 'var(--font-ui)' }}>
          <details>
            <summary style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)', marginBottom: 'var(--space-3)', fontWeight: 600, cursor: 'pointer' }}>
              Controls
            </summary>
            {parsedControls.length ? (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-2)' }}>
                {parsedControls.map((c) => (
                  <li
                    key={c}
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-input)',
                      padding: '4px var(--space-2)',
                      textAlign: 'center',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 'var(--font-size-3xs)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {c}
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: 'var(--font-size-2xs)' }}>No controls mapped.</p>
            )}
          </details>
        </div>
      </section>

      <section style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', background: 'var(--surface)', padding: 'var(--space-4)', fontFamily: 'var(--font-ui)' }}>
        <details>
          <summary style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)', marginBottom: 'var(--space-3)', fontWeight: 600, cursor: 'pointer' }}>
            Framework mappings
          </summary>
          {frameworkMappings.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: 'var(--font-size-2xs)' }}>
              No cross-references recorded yet.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 'var(--font-size-2xs)' }}>
              {frameworkMappings.map((m, i) => (
                <li
                  key={i}
                  style={{
                    padding: 'var(--space-2) 0',
                    borderTop: i > 0 ? '1px solid var(--border)' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 'var(--space-4)',
                    alignItems: 'flex-start',
                  }}
                >
                  <div>
                    <div style={{ color: 'var(--text)', fontWeight: 500 }}>
                      {m.framework_name}{' '}
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)', marginLeft: '4px' }}>
                        {m.control_ref}
                      </span>
                    </div>
                    <div style={{ color: 'var(--text-muted)' }}>{m.control_title}</div>
                  </div>
                  <span
                    style={{
                      fontSize: 'var(--font-size-3xs)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'var(--text-dim)',
                    }}
                  >
                    {m.relationship.replace('_', ' ')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </details>
      </section>
    </div>
  );
}

// ---- Assignee section (B4-8) ----------------------------------------

function AssigneeSection({
  assignee,
  members,
  canManage,
  saving,
  error,
  onAssign,
}: {
  assignee: AssigneeState | null;
  members: WorkspaceMember[] | null;
  canManage: boolean;
  saving: boolean;
  error: string | null;
  onAssign: (userId: string | null) => void;
}) {
  const [pendingUserId, setPendingUserId] = useState<string | ''>(
    assignee?.user_id ?? ''
  );

  // Sync local dropdown when the loaded assignee changes.
  useEffect(() => {
    setPendingUserId(assignee?.user_id ?? '');
  }, [assignee?.user_id]);

  const dirty = pendingUserId !== (assignee?.user_id ?? '');

  return (
    <section
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
        fontFamily: 'var(--font-ui)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <div>
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>
          Assignee
        </h2>
        <p
          style={{
            margin: 'var(--space-1) 0 0',
            color: 'var(--text-dim)',
            fontSize: 'var(--font-size-3xs)',
          }}
        >
          Pick a teammate who owns closing this gap. Assignees are members
          of this workspace; if the person you want isn't listed, invite
          them in Team settings first.
        </p>
      </div>
      {error ? <Alert variant="error">{error}</Alert> : null}
      {assignee ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
            alignItems: 'center',
          }}
        >
          <span style={{ color: 'var(--text)' }}>
            Currently assigned to <strong>{assignee.display_name}</strong>{' '}
            <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
              ({assignee.email})
            </span>
          </span>
        </div>
      ) : (
        <div style={{ color: 'var(--text-dim)' }}>Not assigned.</div>
      )}
      {canManage ? (
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <select
            value={pendingUserId}
            onChange={(e) => setPendingUserId(e.target.value)}
            disabled={saving || members == null}
            style={{
              padding: 'var(--space-2)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-2xs)',
              minWidth: 200,
              background: 'var(--surface)',
              color: 'var(--text)',
            }}
          >
            <option value="">— Unassigned —</option>
            {(members ?? []).map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.display_name} ({m.email})
              </option>
            ))}
          </select>
          <Button
            variant="primary"
            onClick={() => onAssign(pendingUserId === '' ? null : pendingUserId)}
            disabled={!dirty || saving}
          >
            {saving ? 'Saving…' : 'Save assignee'}
          </Button>
        </div>
      ) : (
        <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
          You need Editor or higher to change the assignee.
        </p>
      )}
    </section>
  );
}

// ---- Suggested targets (Phase 5 of readiness-scoring) ----------------
// One row per framework this register item touches, showing the
// per-framework suggested maturity target. Frameworks the workspace
// is actively PURSUING certification for render emphasized; frameworks
// this item touches but the workspace isn't pursuing render dimmed
// with a "not pursued" tag (auditor-friendly — the reader can see the
// full picture, not just what's in scope for this org's goals).
//
// The "Suggested" number at the top is the max target across pursued
// frameworks — so a customer pursuing FedRAMP (L4) + Legislation (L2)
// sees L4, and a customer pursuing Legislation-only sees L2.

interface FrameworkMaturityTargets {
  [frameworkId: string]: number | undefined;
  // A '*' key is a catch-all default for frameworks not explicitly
  // listed; falls back further to evidence-type-derived required
  // level when even '*' is missing.
}

function parseFrameworkTargets(json: string | null): FrameworkMaturityTargets {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as FrameworkMaturityTargets;
    }
    return {};
  } catch {
    return {};
  }
}

// Mirror of core's requiredLevel(). Kept small to avoid pulling core
// into the SPA bundle; core is authoritative and tested.
function derivedLevelFromEvidenceTypes(types: string[]): MaturityLevel {
  const EVIDENCE_TYPE_LEVEL: Record<string, MaturityLevel> = {
    policy: 2,
    process: 3,
    technical: 3,
    monitoring: 4,
    freeform: 1,
  };
  let max: MaturityLevel = 2;
  for (const t of types) {
    const l = EVIDENCE_TYPE_LEVEL[t];
    if (l != null && l > max) max = l;
  }
  return max;
}

function targetForFramework(
  frameworkId: string,
  perFrameworkTargets: FrameworkMaturityTargets,
  expectedEvidenceTypesJson: string
): MaturityLevel {
  const explicit = perFrameworkTargets[frameworkId];
  if (explicit != null) return Math.max(1, Math.min(5, explicit)) as MaturityLevel;
  const wildcard = perFrameworkTargets['*'];
  if (wildcard != null) return Math.max(1, Math.min(5, wildcard)) as MaturityLevel;
  const types = (() => {
    try {
      const parsed = JSON.parse(expectedEvidenceTypesJson);
      return Array.isArray(parsed) ? (parsed as string[]) : [];
    } catch {
      return [];
    }
  })();
  return derivedLevelFromEvidenceTypes(types);
}

const SUGGESTED_LEVEL_NAME: Record<MaturityLevel, string> = {
  1: 'Ad hoc',
  2: 'Documented',
  3: 'Enforced',
  4: 'Monitored',
  5: 'Optimizing',
};

function SuggestedTargetsSection({
  item,
  appliesToFrameworks,
  pursuedFrameworks,
  applicableFrameworks,
  allFrameworks,
}: {
  item: { framework_maturity_targets: string | null; expected_evidence_types: string };
  appliesToFrameworks: string[];
  pursuedFrameworks: string[];
  applicableFrameworks: Framework[];
  allFrameworks: Framework[];
}) {
  const rows = useMemo(() => {
    const perFrameworkTargets = parseFrameworkTargets(item.framework_maturity_targets);
    // If the workspace hasn't declared a posture yet, treat every
    // applicable framework as "pursued" — matches the default we
    // documented (empty posture = pursuing everything applicable).
    const pursuedSet = new Set(
      pursuedFrameworks.length > 0
        ? pursuedFrameworks
        : applicableFrameworks.map((f) => f.framework_id)
    );
    const applicableSet = new Set(applicableFrameworks.map((f) => f.framework_id));
    const nameFor = new Map(
      allFrameworks.map((f) => [f.framework_id, f.short_name || f.name])
    );
    return appliesToFrameworks.map((fwId) => {
      const target = targetForFramework(
        fwId,
        perFrameworkTargets,
        item.expected_evidence_types
      );
      return {
        framework_id: fwId,
        display_name: nameFor.get(fwId) ?? fwId,
        target,
        pursued: pursuedSet.has(fwId),
        applicable: applicableSet.has(fwId),
      };
    });
  }, [
    item.framework_maturity_targets,
    item.expected_evidence_types,
    appliesToFrameworks,
    pursuedFrameworks,
    applicableFrameworks,
    allFrameworks,
  ]);

  const suggestedFromPursued = rows
    .filter((r) => r.pursued)
    .reduce<MaturityLevel>((max, r) => (r.target > max ? r.target : max), 1);
  const suggested = suggestedFromPursued > 0
    ? suggestedFromPursued
    : rows.reduce<MaturityLevel>((max, r) => (r.target > max ? r.target : max), 1);

  return (
    <section
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
        fontFamily: 'var(--font-ui)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', alignItems: 'baseline' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>
            Suggested target
          </h2>
          <p
            style={{
              margin: 'var(--space-1) 0 0',
              color: 'var(--text-dim)',
              fontSize: 'var(--font-size-3xs)',
            }}
          >
            Maximum target maturity across the frameworks you're pursuing —
            the number to aim for if you want this item to count as closed
            for your accreditation goals.
          </p>
        </div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 10px',
            borderRadius: '999px',
            background: 'var(--accent-bg, var(--surface-alt, var(--surface)))',
            color: 'var(--accent-fg, var(--text))',
            border: '1px solid var(--accent, var(--border-strong, var(--border)))',
            fontSize: 'var(--font-size-3xs)',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}
        >
          L{suggested} · {SUGGESTED_LEVEL_NAME[suggested]}
        </span>
      </div>

      {rows.length === 0 ? (
        <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-2xs)' }}>
          This item isn't mapped to any framework yet.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '4px' }}>
          {rows.map((r) => {
            const isPursued = r.pursued;
            const isReference = !isPursued;
            return (
              <li
                key={r.framework_id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 'var(--space-3)',
                  alignItems: 'center',
                  padding: '6px var(--space-3)',
                  borderRadius: 'var(--radius-input)',
                  background: isPursued
                    ? 'var(--surface-alt, var(--surface))'
                    : 'transparent',
                  border: '1px solid var(--border)',
                  opacity: isReference ? 0.65 : 1,
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 'var(--space-2)', minWidth: 0 }}>
                  <span
                    style={{
                      color: 'var(--text)',
                      fontWeight: isPursued ? 600 : 400,
                      fontSize: 'var(--font-size-2xs)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {r.display_name}
                  </span>
                  {isPursued ? (
                    <span
                      style={{
                        fontSize: 'var(--font-size-3xs)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: 'var(--accent-fg, var(--text-muted))',
                        fontWeight: 500,
                      }}
                    >
                      Pursuing
                    </span>
                  ) : (
                    <span
                      style={{
                        fontSize: 'var(--font-size-3xs)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: 'var(--text-dim)',
                      }}
                    >
                      {r.applicable ? 'Not pursued' : 'Reference'}
                    </span>
                  )}
                </span>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    color: isPursued ? 'var(--text)' : 'var(--text-dim)',
                    fontWeight: isPursued ? 600 : 400,
                    fontSize: 'var(--font-size-2xs)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  L{r.target} · {SUGGESTED_LEVEL_NAME[r.target]}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <p
        style={{
          margin: 0,
          color: 'var(--text-dim)',
          fontSize: 'var(--font-size-3xs)',
          lineHeight: 'var(--line-height-body)',
        }}
      >
        Change what you're pursuing under{' '}
        <em>Settings → Onboarding → Compliance goals</em>. Set a workspace
        target below to lock a specific level for this item regardless of
        posture.
      </p>
    </section>
  );
}

// ---- Required-maturity target (Phase 2b of readiness-scoring) --------
// Sets the per-workspace override on customer_register_items. Layer 1
// of the effective-required-level stack. Writers only; viewers see the
// current target but can't change it. When null, the effective target
// falls back to editorial framework maturity target then to the
// evidence-type-derived floor (max of expected_evidence_types).

const MATURITY_TARGET_NAME: Record<MaturityLevel, string> = {
  1: 'L1 — Ad hoc',
  2: 'L2 — Documented',
  3: 'L3 — Enforced',
  4: 'L4 — Monitored',
  5: 'L5 — Optimizing',
};

function RequiredMaturitySection({
  workspaceSlug,
  registerItemId,
  canManage,
  override,
  onChanged,
}: {
  workspaceSlug: string;
  registerItemId: string;
  canManage: boolean;
  override: RequiredMaturityOverride | null;
  onChanged: (next: RequiredMaturityOverride | null) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [level, setLevel] = useState<'' | '1' | '2' | '3' | '4' | '5'>(
    override ? (String(override.level) as '1') : ''
  );
  const [reason, setReason] = useState(override?.reason ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLevel(override ? (String(override.level) as '1') : '');
    setReason(override?.reason ?? '');
  }, [override?.level, override?.reason]);

  async function submit(clear: boolean) {
    setSaving(true);
    setError(null);
    try {
      if (clear) {
        await setRequiredMaturity(workspaceSlug, registerItemId, {
          required_maturity_override: null,
          reason: null,
        });
        onChanged(null);
      } else {
        if (!level || !reason.trim()) return;
        await setRequiredMaturity(workspaceSlug, registerItemId, {
          required_maturity_override: Number(level) as MaturityLevel,
          reason: reason.trim(),
        });
        onChanged({
          level: Number(level) as MaturityLevel,
          reason: reason.trim(),
          set_at: new Date().toISOString(),
          set_by_user_id: null,
        });
      }
      setExpanded(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save target');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
        fontFamily: 'var(--font-ui)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <div>
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>
          Required maturity target
        </h2>
        <p
          style={{
            margin: 'var(--space-1) 0 0',
            color: 'var(--text-dim)',
            fontSize: 'var(--font-size-3xs)',
          }}
        >
          The maturity level this control must reach to count as closed for
          your workspace. When unset, the scoring engine falls back to the
          editorial framework target (e.g. FedRAMP defaults to L4), then to
          the level implied by the evidence types this control expects.
        </p>
      </div>

      {override ? (
        <div style={{ color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>
          Target locked at <strong>{MATURITY_TARGET_NAME[override.level]}</strong>{' '}
          <span style={{ color: 'var(--text-dim)' }}>
            — {override.reason ?? 'no reason recorded'}
            {override.set_at ? ` · ${fmtDate(override.set_at)}` : ''}
          </span>
        </div>
      ) : (
        <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-2xs)' }}>
          Using default target (editorial framework target, then evidence-type floor).
        </div>
      )}

      {!canManage ? (
        <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
          You need Editor or higher to change the target.
        </p>
      ) : !expanded ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setExpanded(true)}>
            {override ? 'Edit target' : 'Set workspace target'}
          </Button>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gap: 'var(--space-2)',
            padding: 'var(--space-3)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-input)',
          }}
        >
          {error ? <Alert variant="error">{error}</Alert> : null}
          <Field id="req-level" label="Required maturity (1–5)">
            <select
              id="req-level"
              value={level}
              onChange={(e) => setLevel(e.target.value as '' | '1' | '2' | '3' | '4' | '5')}
              style={{
                width: '220px',
                padding: 'var(--space-2)',
                borderRadius: 'var(--radius-input)',
                border: '1px solid var(--border)',
                background: 'var(--surface-alt, var(--surface))',
                color: 'var(--text)',
              }}
            >
              <option value="">— pick —</option>
              <option value="1">{MATURITY_TARGET_NAME[1]}</option>
              <option value="2">{MATURITY_TARGET_NAME[2]}</option>
              <option value="3">{MATURITY_TARGET_NAME[3]}</option>
              <option value="4">{MATURITY_TARGET_NAME[4]}</option>
              <option value="5">{MATURITY_TARGET_NAME[5]}</option>
            </select>
          </Field>
          <Field id="req-reason" label="Reason (required)">
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. FedRAMP High requires L4 monitoring for this control family — locking so auditors see the elevated bar."
            />
          </Field>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
            <div>
              {override ? (
                <Button variant="secondary" onClick={() => submit(true)} disabled={saving}>
                  {saving ? 'Clearing…' : 'Clear target'}
                </Button>
              ) : null}
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <Button variant="secondary" onClick={() => setExpanded(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => submit(false)}
                disabled={saving || !level || !reason.trim()}
              >
                {saving ? 'Saving…' : 'Save target'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function FactRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
      <span style={{ color: 'var(--text-dim)' }}>{label}</span>
      <span style={{ color: 'var(--text)', textAlign: 'right' }}>{value || '—'}</span>
    </div>
  );
}

function safeParseArray(json: string): string[] {
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// ============================================================
// Evidence section — E3 of evidence-model-plan.md.
// ============================================================
// Read-only surface plus an inline Attest action per binding. Wraps
// GET /api/w/:slug/register/:item_id/evidence and
// POST /api/w/:slug/register/:item_id/evidence/:binding_id/attest.
// Artifact creation lives in the (future) library page — E4.

type EvidenceType = 'policy' | 'technical' | 'monitoring' | 'process' | 'freeform';
type Freshness = 'never' | 'current' | 'due_soon' | 'overdue';

interface EvidenceBindingRow {
  binding_id: string;
  type: EvidenceType;
  gap_notes: string | null;
  attestation_cadence_months: number;
  last_attested_at: string | null;
  on_time_streak: number;
  artifact_review_current: boolean;
  freshness: Freshness;
}

interface EvidenceBundleResponse {
  risk_item_id: string;
  system_id: string | null;
  derived_maturity_level: 1 | 2 | 3 | 4 | 5;
  // E7: effective (override or derived), per-system rollup + text,
  // and current override state if any.
  effective_maturity_level: 1 | 2 | 3 | 4 | 5;
  per_system_maturity: Array<{
    system_id: string;
    system_name: string;
    classification_types: string[];
    level: 1 | 2 | 3 | 4 | 5;
    freshness_worst: Freshness;
    binding_count: number;
  }>;
  rollup_text: string;
  maturity_override: {
    level: 1 | 2 | 3 | 4 | 5;
    reason: string | null;
  } | null;
  bindings: EvidenceBindingRow[];
}

const TYPE_LABEL: Record<EvidenceType, string> = {
  policy: 'Policy',
  technical: 'Technical control',
  monitoring: 'Monitoring',
  process: 'Process record',
  freeform: 'Note',
};

const TYPE_ORDER: EvidenceType[] = [
  'policy',
  'technical',
  'process',
  'monitoring',
  'freeform',
];

const FRESHNESS_COPY: Record<Freshness, { text: string; tone: 'ok' | 'warn' | 'bad' | 'muted' }> = {
  never: { text: 'Never attested', tone: 'bad' },
  current: { text: 'Current', tone: 'ok' },
  due_soon: { text: 'Due soon', tone: 'warn' },
  overdue: { text: 'Overdue', tone: 'bad' },
};

const MATURITY_NAME: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: 'Ad hoc',
  2: 'Documented',
  3: 'Enforced',
  4: 'Monitored',
  5: 'Optimizing',
};

function EvidenceSection({
  workspaceSlug,
  registerItemId,
  canManage,
  expectedEvidenceTypesJson,
  category,
}: {
  workspaceSlug: string;
  registerItemId: string;
  canManage: boolean;
  expectedEvidenceTypesJson: string;
  category: string;
}) {
  const expectedTypes = parseExpectedTypes(expectedEvidenceTypesJson, category);
  const [bundle, setBundle] = useState<EvidenceBundleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = await getRegisterItemEvidence<EvidenceBundleResponse>(workspaceSlug, registerItemId);
      setBundle(body);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Network error loading evidence.');
      setBundle(null);
    } finally {
      setLoading(false);
    }
  }, [workspaceSlug, registerItemId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
        fontFamily: 'var(--font-ui)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
        <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>
            Evidence
          </h2>
          <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
            Typed artifacts backing this control's implementation. Maturity
            is derived from evidence type + attestation freshness — see the
            rubric for definitions.
          </p>
          {bundle && bundle.rollup_text ? (
            <p style={{ margin: 'var(--space-1) 0 0', color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
              {bundle.rollup_text}
            </p>
          ) : null}
          {bundle && bundle.maturity_override ? (
            <p style={{ margin: 0, color: 'var(--warning-fg)', fontSize: 'var(--font-size-3xs)' }}>
              Override L{bundle.maturity_override.level} in effect —{' '}
              <em>{bundle.maturity_override.reason ?? 'no reason recorded'}</em>
            </p>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          {bundle ? (
            <MaturityChip
              level={bundle.effective_maturity_level}
              overridden={bundle.effective_maturity_level !== bundle.derived_maturity_level}
            />
          ) : null}
          {canManage ? (
            <Link
              to={`/w/${workspaceSlug}/app/evidence`}
              style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--secondary)', textDecoration: 'none' }}
            >
              Library ↗
            </Link>
          ) : null}
        </div>
      </div>

      {bundle && canManage ? (
        <MaturityOverrideEditor
          workspaceSlug={workspaceSlug}
          registerItemId={registerItemId}
          currentOverride={bundle.maturity_override}
          derivedLevel={bundle.derived_maturity_level}
          onChanged={load}
        />
      ) : null}

      {bundle && bundle.per_system_maturity.length > 0 ? (
        <PerSystemBreakdown systems={bundle.per_system_maturity} />
      ) : null}

      {error ? <Alert variant="error">{error}</Alert> : null}

      {loading ? (
        <Spinner label="Loading evidence" />
      ) : bundle && bundle.bindings.length > 0 ? (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
          {[...bundle.bindings]
            .sort((a, b) => TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type))
            .map((b) => (
              <EvidenceBindingRowView
                key={b.binding_id}
                binding={b}
                workspaceSlug={workspaceSlug}
                registerItemId={registerItemId}
                canAttest={canManage}
                onAttested={load}
              />
            ))}
        </ul>
      ) : bundle ? (
        <div
          style={{
            padding: 'var(--space-3)',
            border: '1px dashed var(--border)',
            borderRadius: 'var(--radius-input)',
            color: 'var(--text-dim)',
            fontSize: 'var(--font-size-2xs)',
          }}
        >
          No evidence attached yet.
          {expectedTypes.length > 0 ? (
            <>
              {' '}
              <span style={{ color: 'var(--text-muted)' }}>
                This control usually needs {formatExpectedTypes(expectedTypes)} evidence.
              </span>
            </>
          ) : null}
          {canManage ? (
            <>
              {' '}
              <Link
                to={`/w/${workspaceSlug}/app/evidence`}
                style={{ color: 'var(--secondary)' }}
              >
                Open the library
              </Link>{' '}
              to upload a policy or add a monitoring link, then attach it here.
              {expectedTypes.includes('policy') ? (
                <>
                  {' Or '}
                  <Link
                    to={`/w/${workspaceSlug}/app/policies/new?category=${encodeURIComponent(category)}`}
                    style={{ color: 'var(--accent)' }}
                  >
                    draft a policy
                  </Link>
                  {' to accelerate coverage.'}
                </>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {canManage && bundle ? (
        <AttachArtifactPicker
          workspaceSlug={workspaceSlug}
          registerItemId={registerItemId}
          onAttached={load}
          existingArtifactIds={new Set(bundle.bindings.map((b) => b.binding_id))}
        />
      ) : null}
    </section>
  );
}

interface LibraryArtifact {
  artifact_id: string;
  type: EvidenceType;
  title: string;
  version_label: string | null;
}

function AttachArtifactPicker({
  workspaceSlug,
  registerItemId,
  onAttached,
}: {
  workspaceSlug: string;
  registerItemId: string;
  onAttached: () => void;
  existingArtifactIds: Set<string>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [artifacts, setArtifacts] = useState<LibraryArtifact[] | null>(null);
  const [selectedId, setSelectedId] = useState('');
  const [cadence, setCadence] = useState('6');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLibrary = useCallback(async () => {
    setError(null);
    try {
      const body = await listEvidenceArtifacts<{ artifacts: LibraryArtifact[] }>(workspaceSlug);
      setArtifacts(body.artifacts);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Network error loading library.');
    }
  }, [workspaceSlug]);

  useEffect(() => {
    if (expanded && artifacts === null) void loadLibrary();
  }, [expanded, artifacts, loadLibrary]);

  async function submit() {
    if (!selectedId) return;
    setSaving(true);
    setError(null);
    try {
      await attachEvidenceArtifact(workspaceSlug, selectedId, {
        risk_item_id: registerItemId,
        attestation_cadence_months: Number(cadence) || 6,
      });
      setSelectedId('');
      setExpanded(false);
      onAttached();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't attach evidence.");
    } finally {
      setSaving(false);
    }
  }

  if (!expanded) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={() => setExpanded(true)}>
          Attach evidence
        </Button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gap: 'var(--space-2)',
        padding: 'var(--space-3)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-input)',
      }}
    >
      {error ? <Alert variant="error">{error}</Alert> : null}
      {artifacts === null ? (
        <Spinner label="Loading library" />
      ) : artifacts.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
          The library is empty.{' '}
          <Link to={`/w/${workspaceSlug}/app/evidence`} style={{ color: 'var(--secondary)' }}>
            Add an artifact first
          </Link>
          , then come back.
        </p>
      ) : (
        <>
          <Field id="attach-artifact" label="Artifact from library">
            <select
              id="attach-artifact"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              style={{
                width: '100%',
                padding: 'var(--space-2)',
                borderRadius: 'var(--radius-input)',
                border: '1px solid var(--border)',
                background: 'var(--surface-alt, var(--surface))',
                color: 'var(--text)',
              }}
            >
              <option value="">— Pick an artifact —</option>
              {artifacts.map((a) => (
                <option key={a.artifact_id} value={a.artifact_id}>
                  [{TYPE_LABEL[a.type]}] {a.title}
                  {a.version_label ? ` (${a.version_label})` : ''}
                </option>
              ))}
            </select>
          </Field>
          <Field id="attach-cadence" label="Attestation cadence (months)">
            <input
              id="attach-cadence"
              type="number"
              min={1}
              max={60}
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              style={{
                width: '120px',
                padding: 'var(--space-2)',
                borderRadius: 'var(--radius-input)',
                border: '1px solid var(--border)',
                background: 'var(--surface-alt, var(--surface))',
                color: 'var(--text)',
              }}
            />
          </Field>
        </>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
        <Button variant="secondary" onClick={() => setExpanded(false)} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={submit}
          disabled={saving || !selectedId || (artifacts?.length ?? 0) === 0}
        >
          {saving ? 'Attaching…' : 'Attach'}
        </Button>
      </div>
    </div>
  );
}

function MaturityChip({
  level,
  overridden = false,
}: {
  level: 1 | 2 | 3 | 4 | 5;
  overridden?: boolean;
}) {
  const bg =
    level >= 4 ? 'var(--success-bg)' : level >= 2 ? 'var(--warning-bg)' : 'var(--danger-bg)';
  const fg =
    level >= 4 ? 'var(--success-fg)' : level >= 2 ? 'var(--warning-fg)' : 'var(--danger-fg)';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        border: overridden ? '1px dashed var(--warning-fg)' : 'none',
        padding: '4px 10px',
        borderRadius: '999px',
        background: bg,
        color: fg,
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
      title={`Rubric: ${MATURITY_NAME[level]}. See docs/evidence-maturity-rubric.md.`}
    >
      L{level} · {MATURITY_NAME[level]}
    </span>
  );
}

function EvidenceBindingRowView({
  binding,
  workspaceSlug,
  registerItemId,
  canAttest,
  onAttested,
}: {
  binding: EvidenceBindingRow;
  workspaceSlug: string;
  registerItemId: string;
  canAttest: boolean;
  onAttested: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      await attestEvidenceBinding(workspaceSlug, registerItemId, binding.binding_id, comment ? { comment } : {});
      setComment('');
      setExpanded(false);
      onAttested();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't attest evidence.");
    } finally {
      setSaving(false);
    }
  }

  const fresh = FRESHNESS_COPY[binding.freshness];

  return (
    <li
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-input)',
        padding: 'var(--space-3)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
        <div style={{ display: 'grid', gap: '2px' }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text)', fontWeight: 500 }}>
              {TYPE_LABEL[binding.type]}
            </span>
            <FreshnessChip freshness={binding.freshness} label={fresh.text} tone={fresh.tone} />
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
              Cadence {binding.attestation_cadence_months}mo
            </span>
          </div>
          <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
            {binding.last_attested_at
              ? `Last attested ${fmtDate(binding.last_attested_at)}`
              : 'Never attested'}
            {binding.gap_notes ? (
              <>
                {' · '}
                <span style={{ color: 'var(--warning-fg)' }}>
                  Gap: {binding.gap_notes}
                </span>
              </>
            ) : null}
          </div>
        </div>
        {canAttest ? (
          <Button
            variant={expanded ? 'secondary' : 'primary'}
            onClick={() => setExpanded((v) => !v)}
            disabled={saving}
          >
            {expanded ? 'Cancel' : 'Attest'}
          </Button>
        ) : null}
      </div>

      {expanded ? (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          {error ? <Alert variant="error">{error}</Alert> : null}
          <Field id={`attest-${binding.binding_id}`} label="Comment (optional)">
            <Textarea
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="e.g. Reviewed 2026-Q2, no gaps."
            />
          </Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="primary" onClick={submit} disabled={saving}>
              {saving ? 'Recording…' : 'Record attestation'}
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function FreshnessChip({
  label,
  tone,
}: {
  freshness: Freshness;
  label: string;
  tone: 'ok' | 'warn' | 'bad' | 'muted';
}) {
  const palette: Record<typeof tone, { bg: string; fg: string }> = {
    ok: { bg: 'var(--success-bg)', fg: 'var(--success-fg)' },
    warn: { bg: 'var(--warning-bg)', fg: 'var(--warning-fg)' },
    bad: { bg: 'var(--danger-bg)', fg: 'var(--danger-fg)' },
    muted: { bg: 'var(--surface-alt)', fg: 'var(--text-dim)' },
  };
  const c = palette[tone];
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: '999px',
        background: c.bg,
        color: c.fg,
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 500,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

// ============================================================
// E7 — per-system maturity breakdown + override editor.
// ============================================================

function PerSystemBreakdown({
  systems,
}: {
  systems: EvidenceBundleResponse['per_system_maturity'];
}) {
  const worstLevel = systems.reduce(
    (min, s) => (s.level < min ? s.level : min),
    5 as 1 | 2 | 3 | 4 | 5
  );
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-input)',
        padding: 'var(--space-3)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline' }}>
        <span style={{ color: 'var(--text)', fontWeight: 500, fontSize: 'var(--font-size-2xs)' }}>
          Per-system maturity
        </span>
        <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
          Rolled up as the min across applicable systems (auditor-safe).
        </span>
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '4px' }}>
        {systems.map((s) => (
          <li
            key={s.system_id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 'var(--space-3)',
              fontSize: 'var(--font-size-3xs)',
              padding: '2px 0',
            }}
          >
            <span style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline' }}>
              <span style={{ color: 'var(--text)' }}>{s.system_name}</span>
              <span style={{ color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                {s.classification_types.join(', ')}
              </span>
              <span style={{ color: 'var(--text-dim)' }}>
                {s.binding_count} binding{s.binding_count === 1 ? '' : 's'}
              </span>
            </span>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                color: s.level === worstLevel ? 'var(--warning-fg)' : 'var(--text)',
              }}
            >
              L{s.level}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MaturityOverrideEditor({
  workspaceSlug,
  registerItemId,
  currentOverride,
  derivedLevel,
  onChanged,
}: {
  workspaceSlug: string;
  registerItemId: string;
  currentOverride: { level: 1 | 2 | 3 | 4 | 5; reason: string | null } | null;
  derivedLevel: 1 | 2 | 3 | 4 | 5;
  onChanged: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [level, setLevel] = useState<'' | '1' | '2' | '3' | '4' | '5'>(
    currentOverride ? String(currentOverride.level) as '1' : ''
  );
  const [reason, setReason] = useState(currentOverride?.reason ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(clear: boolean) {
    setSaving(true);
    setError(null);
    try {
      const payload = clear
        ? { level: null }
        : { level: Number(level), reason };
      await setEvidenceMaturityOverride(workspaceSlug, registerItemId, payload);
      setExpanded(false);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save override.");
    } finally {
      setSaving(false);
    }
  }

  if (!expanded) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={() => setExpanded(true)}>
          {currentOverride ? 'Edit override' : 'Override maturity'}
        </Button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gap: 'var(--space-2)',
        padding: 'var(--space-3)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-input)',
      }}
    >
      <div style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text)' }}>
        Override maturity{' '}
        <span style={{ color: 'var(--text-dim)' }}>
          — derived is L{derivedLevel}. Overrides are recorded in the audit trail
          and downgrade the drafter's cited confidence one step.
        </span>
      </div>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <Field id="override-level" label="Level (1–5)">
        <select
          id="override-level"
          value={level}
          onChange={(e) => setLevel(e.target.value as '' | '1' | '2' | '3' | '4' | '5')}
          style={{
            width: '120px',
            padding: 'var(--space-2)',
            borderRadius: 'var(--radius-input)',
            border: '1px solid var(--border)',
            background: 'var(--surface-alt, var(--surface))',
            color: 'var(--text)',
          }}
        >
          <option value="">— pick —</option>
          <option value="1">L1 — Ad hoc</option>
          <option value="2">L2 — Documented</option>
          <option value="3">L3 — Enforced</option>
          <option value="4">L4 — Monitored</option>
          <option value="5">L5 — Optimizing</option>
        </select>
      </Field>
      <Field id="override-reason" label="Reason (required)">
        <Textarea
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Monitoring signal covered by external SOC 2 audit report; artifact not stored in gacpro."
        />
      </Field>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
        <div>
          {currentOverride ? (
            <Button variant="secondary" onClick={() => submit(true)} disabled={saving}>
              {saving ? 'Clearing…' : 'Clear override'}
            </Button>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Button variant="secondary" onClick={() => setExpanded(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => submit(false)}
            disabled={saving || !level || !reason.trim()}
          >
            {saving ? 'Saving…' : 'Save override'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---- E9: expected-evidence type helpers ------------------------------
// Client-side mirror of core's readExpectedEvidenceTypes / defaults.
// Kept small to avoid pulling the whole core bundle into the SPA — the
// core function is the authoritative version and is covered by tests.

const CATEGORY_DEFAULT_TYPES: Record<string, EvidenceType[]> = {
  fairness_bias: ['policy', 'monitoring'],
  safety: ['policy', 'monitoring'],
  audit_logging: ['policy', 'monitoring'],
  content_safety: ['policy', 'monitoring'],
  data_governance: ['policy', 'technical'],
  privacy: ['policy', 'technical'],
  access_control: ['policy', 'technical'],
  security: ['policy', 'technical', 'monitoring'],
  supply_chain: ['policy', 'technical', 'process'],
  governance: ['policy', 'process'],
  intellectual_property: ['policy', 'process'],
  transparency: ['policy', 'process'],
  incident_response: ['policy', 'process'],
  change_management: ['policy', 'process'],
  environmental: ['policy', 'process'],
};

function parseExpectedTypes(json: string, category: string): EvidenceType[] {
  const allowed: EvidenceType[] = ['policy', 'technical', 'monitoring', 'process'];
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) {
      const filtered = parsed.filter((v): v is EvidenceType =>
        typeof v === 'string' && (allowed as string[]).includes(v)
      );
      if (filtered.length > 0) return filtered;
    }
  } catch {
    /* fall through to category default */
  }
  return CATEGORY_DEFAULT_TYPES[category] ?? ['policy'];
}

function formatExpectedTypes(types: EvidenceType[]): string {
  const labels = types.map((t) => TYPE_LABEL[t]);
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} + ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')} + ${labels[labels.length - 1]}`;
}

// ---- V89-6-2: Connector evidence indicator -------------------------
// Shows whether connector evidence (control_checks with OSCAL
// requirement_ref) exists for this register item's mapped controls.

function formatRelativeTime(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(isoDate).toLocaleDateString();
}

function ConnectorEvidenceIndicator({
  workspaceSlug,
  itemId,
}: {
  workspaceSlug: string;
  itemId: string;
}) {
  const [data, setData] = useState<ControlEvidenceResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getControlEvidence(workspaceSlug, itemId);
        if (!cancelled) setData(result);
      } catch {
        /* silent — indicator just won't render */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceSlug, itemId]);

  if (loading || !data) return null;

  // Deduplicate connector kinds for display
  const connectorKinds = data.has_evidence
    ? [...new Set(data.evidence.map((e) => e.connector_kind))]
    : [];

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-3)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        fontFamily: 'var(--font-ui)',
        fontSize: 'var(--font-size-2xs)',
      }}
    >
      {data.has_evidence ? (
        <>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
            style={{ flexShrink: 0 }}
          >
            <path
              d="M8 1.5C6.2 1.5 4.7 2.3 3.8 3.5C2 4 .5 5.7.5 7.8c0 2.5 2 4.5 4.5 4.7h6.5c2 0 3.5-1.6 3.5-3.5 0-1.6-1.1-3-2.6-3.4C11.8 3.4 10.1 1.5 8 1.5z"
              stroke="var(--band-green, var(--success-fg))"
              strokeWidth="1.2"
              fill="none"
            />
            <path
              d="M5.5 8l2 2 3-3.5"
              stroke="var(--band-green, var(--success-fg))"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
          <span style={{ color: 'var(--text)' }}>
            Last verified {formatRelativeTime(data.last_checked!)} via{' '}
            <span style={{ fontWeight: 500 }}>
              {connectorKinds.join(', ')}
            </span>
          </span>
          <span
            style={{
              color: 'var(--text-dim)',
              fontSize: 'var(--font-size-3xs)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {data.evidence.length} check{data.evidence.length === 1 ? '' : 's'}
          </span>
        </>
      ) : (
        <span style={{ color: 'var(--text-muted)' }}>
          No connector evidence
        </span>
      )}
    </div>
  );
}

function LiveCheckSection({ controlId }: { workspaceSlug?: string; controlId: string }) {
  const checks = useWorkspaceQuery(
    (slug) => [...queryKeys.controlChecks(slug), controlId],
    (slug) => listControlChecks(slug, controlId, 5).catch(() => ({ checks: [] })),
  );

  if (checks.loading) return null;
  if (!checks.data || checks.data.checks.length === 0) return null;

  const latest = checks.data.checks[0];
  // LM-21: a Tested rule's result is a preview — neutral, labelled, and never read as pass / fail.
  const chipColor =
    latest.preview ? 'var(--text-muted)'
    : latest.status === 'pass' ? 'var(--success)'
    : latest.status === 'fail' ? 'var(--critical)'
    : 'var(--warning)';

  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
      <div style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-2xs)', fontWeight: 600, color: 'var(--text)' }}>
        Live checks
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            padding: '2px 8px',
            borderRadius: 'var(--radius-full, 999px)',
            fontSize: 'var(--font-size-3xs)',
            fontWeight: 500,
            color: chipColor,
            border: `1px solid ${chipColor}`,
            background: 'var(--surface)',
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: chipColor }} />
          {latest.status}
        </span>
        {latest.preview ? <Badge>Preview</Badge> : null}
        <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
          {new Date(latest.observed_at).toLocaleDateString()} · v{latest.collector_version}
        </span>
      </div>
      {/* V7-4-6: Evidence provenance display when the API returns provenance fields */}
      {latest.provenance_level && (
        <EvidenceProvenance
          provenance_level={latest.provenance_level as 'unsigned' | 'oidc_verified' | 'sigstore_signed'}
          emitter_identity={latest.emitter_identity ?? undefined}
          emitter_signature_verified={!!latest.emitter_signature_verified}
          ci_run_url={latest.ci_run_url ?? undefined}
          submission_timestamp={latest.observed_at}
        />
      )}
      {checks.data.checks.length > 1 ? (
        <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
          {checks.data.checks.length - 1} earlier check{checks.data.checks.length > 2 ? 's' : ''}
        </div>
      ) : null}
    </div>
  );
}
